/**
 * 1stbuddha — menu-drawer.js
 *
 * <menu-drawer> (snippets/menu-drawer.liquid) handles the sub-panels inside
 * the menu drawer. A row button with aria-controls opens its panel, which
 * slides over the main list; the main list is made inert while a panel is
 * open. [data-submenu-close] (the back button) returns to the main list and
 * restores focus to the row. Panels reset when the drawer closes.
 */
(() => {
  if (customElements.get('menu-drawer')) return;

  // Returns milliseconds. Accepts "240ms" and ".24s": Shopify's CDN minifies
  // base.css and may rewrite ms values as seconds.
  const readDuration = (token, fallback) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return fallback;
    return /\ds$/i.test(raw) && !/ms$/i.test(raw) ? value * 1000 : value;
  };

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('.menu-drawer__main');

      this.addEventListener('click', (event) => {
        const row = event.target.closest('.menu-drawer__row[aria-controls]');
        if (row) {
          this.openPanel(row);
          return;
        }
        if (event.target.closest('[data-submenu-close]')) this.closePanel();
      });

      this.addEventListener('keydown', (event) => {
        // Esc inside a sub-panel goes back one level instead of closing the drawer.
        if (event.key === 'Escape' && this.activePanel) {
          event.preventDefault();
          event.stopPropagation();
          this.closePanel();
        }
      });

      this.closest('dialog-component')?.addEventListener('dialog:close', () => this.closePanel(false));
    }

    openPanel(row) {
      const panel = document.getElementById(row.getAttribute('aria-controls'));
      if (!panel) return;

      this.activeRow = row;
      this.activePanel = panel;
      row.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      void panel.offsetWidth;
      panel.dataset.open = 'true';
      this.main.inert = true;
      this.scrollTop = 0;
      panel.querySelector('[data-submenu-close]')?.focus({ preventScroll: true });
    }

    closePanel(restoreFocus = true) {
      const { activePanel: panel, activeRow: row } = this;
      if (!panel) return;

      this.activePanel = null;
      this.activeRow = null;
      this.main.inert = false;
      row?.setAttribute('aria-expanded', 'false');
      delete panel.dataset.open;

      if (restoreFocus) {
        row?.focus({ preventScroll: true });
        setTimeout(() => {
          if (panel.dataset.open !== 'true') panel.hidden = true;
        }, readDuration('--duration-base', 240));
      } else {
        panel.hidden = true;
      }
    }
  }

  customElements.define('menu-drawer', MenuDrawer);
})();
